import React, { useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PHRASES = {
  it: [
    'La ricchezza premia chi non molla: alza la cornetta.',
    'Ogni "no" ti avvicina al prossimo "sì" da migliaia di euro.',
    'I soldi seguono le persone che agiscono, non quelle che aspettano.',
    'Il tuo prossimo cliente sta aspettando la tua chiamata proprio ora.',
    'La disciplina batte il talento quando il talento non chiama.',
    'Non contare le chiamate, fai in modo che ogni chiamata conti.',
    'Chi crea ricchezza si sveglia deciso, non speranzoso.',
    'Ogni squillo è un\'opportunità. Ogni chiamata è un mattone del tuo impero.',
    'Il denaro ama la velocità: chiudi ora, festeggia dopo.',
    'Sii così bravo che non possano ignorarti — nemmeno al telefono.',
    'La libertà finanziaria comincia con la prossima chiamata.',
    'I campioni non aspettano il momento giusto: lo creano.',
    'Vinci la giornata: 10 chiamate in più, 1 vita diversa.',
    'La differenza tra chi ce la fa e chi no è una sola chiamata.',
    'Fatturato oggi, libertà domani.',
    'Non esistono lead cattivi, esistono solo chiamate senza convinzione.',
    'Chiudi il deal. Poi il prossimo. Poi il prossimo ancora.',
    'Ogni euro incassato è un voto di fiducia nel tuo lavoro.',
    'Ti pagano per creare valore, non per contare i minuti.',
    'Il successo è la somma di piccoli sforzi ripetuti ogni giorno.',
  ],
  en: [
    'Wealth rewards those who don\'t quit: pick up the phone.',
    'Every "no" brings you closer to the next thousand-euro "yes".',
    'Money follows people who act, not people who wait.',
    'Your next customer is waiting for your call right now.',
    'Discipline beats talent when talent doesn\'t call.',
    'Don\'t count the calls, make every call count.',
    'Wealth builders wake up determined, not hopeful.',
    'Every ring is an opportunity. Every call is a brick in your empire.',
    'Money loves speed: close now, celebrate later.',
    'Be so good they can\'t ignore you — not even on the phone.',
    'Financial freedom starts with your next call.',
    'Champions don\'t wait for the right moment: they create it.',
    'Win the day: 10 more calls, a different life.',
    'The gap between winners and losers is one more call.',
    'Revenue today, freedom tomorrow.',
    'There are no bad leads, only calls without conviction.',
    'Close the deal. Then the next. Then the next.',
    'Every euro earned is a vote of confidence in your work.',
    'You get paid to create value, not to count minutes.',
    'Success is small efforts repeated every single day.',
  ],
};

const REFRESH_MS = 60 * 60 * 1000; // 1 hour

const pickPhrase = (lang) => {
  const list = PHRASES[lang] || PHRASES.it;
  return list[Math.floor(Math.random() * list.length)];
};

const MotivationalBanner = ({ theme, userName }) => {
  const { language } = useLanguage();
  const [phrase, setPhrase] = useState(() => pickPhrase(language));
  const [visible, setVisible] = useState(true);
  const [animate, setAnimate] = useState(true);

  // Fresh phrase every time Dashboard mounts (component mount)
  useEffect(() => {
    setPhrase(pickPhrase(language));
    setAnimate(false);
    // trigger reflow to restart animation
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, [language]);

  // Rotate phrase every hour while user stays on dashboard
  useEffect(() => {
    const id = setInterval(() => {
      setPhrase(pickPhrase(language));
      setAnimate(false);
      requestAnimationFrame(() => setAnimate(true));
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [language]);

  if (!visible) return null;

  const isDark = theme === 'dark';
  const bg = isDark
    ? 'linear-gradient(90deg, #1a1200 0%, #3d2f00 45%, #1a1200 100%)'
    : 'linear-gradient(90deg, #FFF3B0 0%, #FFD700 45%, #FFF3B0 100%)';
  const border = isDark ? '#8a6d0f' : '#B8860B';
  const text = isDark ? '#F4C430' : '#5C4300';

  return (
    <div
      data-testid="motivational-strip"
      className={`relative mb-4 rounded-lg border overflow-hidden ${animate ? 'motiv-slide-in' : ''}`}
      style={{
        background: bg,
        borderColor: border,
        boxShadow: isDark ? '0 4px 20px rgba(212, 175, 55, 0.15)' : '0 4px 20px rgba(184, 134, 11, 0.25)',
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <Sparkles size={22} style={{ color: text, flexShrink: 0 }} className="motiv-sparkle" />
        <div className="flex-1 min-w-0">
          <p
            key={phrase}
            data-testid="motivational-phrase"
            className="text-base sm:text-lg font-semibold motiv-text-fade"
            style={{ color: text, lineHeight: 1.35 }}
          >
            {phrase}
          </p>
          {userName && (
            <p className="text-xs opacity-70" style={{ color: text }}>
              — {userName}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          data-testid="motivational-dismiss"
          className="p-1 rounded hover:bg-black/10 transition-colors"
          aria-label="dismiss"
          style={{ color: text }}
        >
          <X size={18} />
        </button>
      </div>
      <style>{`
        @keyframes motivSlideIn {
          from { transform: translateY(-16px); opacity: 0; }
          to   { transform: translateY(0);      opacity: 1; }
        }
        .motiv-slide-in { animation: motivSlideIn 500ms cubic-bezier(.22,.61,.36,1) both; }
        @keyframes motivFade {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .motiv-text-fade { animation: motivFade 600ms ease-out both; }
        @keyframes motivSparkle {
          0%,100% { transform: rotate(0deg) scale(1); }
          50%     { transform: rotate(15deg) scale(1.15); }
        }
        .motiv-sparkle { animation: motivSparkle 2.4s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default MotivationalBanner;
